import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../lib/api';
import { sessionsQueryKey } from './use-sessions';

interface ErrorBody {
  error?: string;
  message?: string;
}

export interface RevokeError extends Error {
  status?: number;
  body?: ErrorBody;
}

function toError(stage: string, response: Response | undefined, error: unknown): RevokeError {
  const e = new Error(stage) as RevokeError;
  if (response) e.status = response.status;
  e.body = (error ?? {}) as ErrorBody;
  return e;
}

/**
 * Revokes a session by id. The backend gates revoking *non-current* sessions
 * on `RequireStepUp` (see `crates/sentinel-web/src/routes/auth/sessions.rs`),
 * so a 403 `step_up_required` is intercepted by the api-client step-up
 * middleware (Task 1), which opens the global dialog and replays the DELETE
 * once verification succeeds. Revoking the current session is treated like a
 * logout: the backend clears auth cookies in the response.
 */
export function useRevokeSession() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<void, RevokeError, { id: string }>({
    mutationFn: async ({ id }) => {
      const { error, response } = await api.DELETE('/auth/sessions/{id}', {
        params: { path: { id } },
      });
      if (error) throw toError('session_revoke_failed', response, error);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: sessionsQueryKey });
    },
  });
}
